<template>
  <v-app>
    <left-nav v-if="currentUser"/>
    <audit-trail-panel v-if="currentUser" />
    <v-main>
      <div class="consolidate-menu" v-show="showOrganizationSelector">
        <div class="inner">
          <organization-select
              clearable
              v-model="selectedOrganization"
              without-filter
          />
          <v-checkbox
              :label="$t('organization.consolidate-organization')"
              v-model="isConsolidate"
              :disabled="consolidateOrganizationDisabled"
              hide-details
          />
        </div>
      </div>


      <v-container fluid  v-if="showContent">
        <keep-alive :key="orgScope" :include="cachedPages" v-if="currentUser">
          <router-view :key="routeViewKey"></router-view>
        </keep-alive>
      </v-container>
    </v-main>
    <Footer/>
    <v-overlay color="white" :value="loading" z-index="99999">
      <v-progress-circular
          color="primary"
          indeterminate
          size="90"
      ></v-progress-circular>
    </v-overlay>
  </v-app>
</template>

<script>


import LeftNav from "@blocks/LeftNav";
import AuditTrailPanel from "@elements/AuditTrailPanel.vue";
import { isMobileDevice, hasFieldPortal } from '@/utils/device'
import Footer from "@blocks/Footer";
import {EventBus} from "@includes/EventBus";
import {mapActions, mapGetters} from "vuex";
import SecurityRepository from "@repository/SecurityRepository";
import OrganizationSelect from "@blocks/Select/OrganizationSelect.vue";


export default {
  components: {
    AuditTrailPanel,
    LeftNav,
    Footer,
    OrganizationSelect,
  },

  data: () => ({
    loading: false,
    isConsolidate: true,
    selectedOrganization: null,
    isConsolidateByUser: false,
    selectedOrganizationByUser: false,
    consolidateOrganizationDisabled: true,
    displayConsolidation:false,
    showContent:true,
    // Cache scope token (used as the keep-alive :key). Changing it tears the whole keep-alive down.
    orgScope: null,
  }),
  watch: {
    // Scope the keep-alive cache to "the org you're currently working in". While you stay on that
    // org (its tabs) or drill down into one of its entity detail pages, the token is stable so the
    // listing stays cached. As soon as you switch org, or go to any other section, the token
    // changes (or resets to null) and the whole cache is dropped — no stale listings lingering.
    $route: {
      immediate: true,
      handler(to) {
        const path = to.matched[1] ? to.matched[1].path : ''
        if (/\/organizations\/:id/.test(path)) {
          this.orgScope = 'org:' + to.params.id
        } else if (/:[^/]+\/?$/.test(path)) {
          // Single-entity detail page (e.g. /vehicles/:id) — a drill-down: keep the current scope.
        } else {
          this.orgScope = null
        }
      },
    },
    selectedOrganization(organizationId) {
      this.consolidateOrganizationDisabled = organizationId === null
      if(this.selectedOrganizationByUser) {
        this.consolidateOrganization()
      }
    },
    isConsolidate(state) {
      if(this.isConsolidateByUser){
        this.consolidateOrganization()
      }
    }
  },
  mounted() {
    EventBus.$on('reloadView', this.reloadView)

    EventBus.$on('loading', this.showLoading)
    // Always try to fetch user - auth is via HttpOnly cookie
    this.showLoading(true)
    this.fetchCurrentUser().then(user => {
      // Set authenticated state
      this.$auth.setAuthenticated(true, user)

      // Le back-office ne fonctionne que sur ordinateur, pour tout le monde (technicien compris). Sur téléphone ou
      // tablette : le portail terrain pour qui y a droit, sinon on arrête là.
      if (isMobileDevice()) {
        this.$router.replace({ name: hasFieldPortal(user) ? 'field' : 'phone-unsupported' })
        return
      }

      this.selectedOrganization = user.selectedOrganizationId;
      this.isConsolidate = user.selectedOrganizationConsolidated;

      this.$nextTick(() => {
        this.selectedOrganizationByUser = true
        this.isConsolidateByUser = true
        this.showLoading(false)
      })

    }).catch(e => {
      if (e.response?.status === 401) {
        // Not authenticated - set state and redirect will be handled by auth.js
        this.$auth.setAuthenticated(false)
      } else {
        this.notifyError(e)
      }
      this.showLoading(false)
    })


  },

  destroyed() {
    EventBus.$off('loading', this.showLoading)
    EventBus.$off('reloadView', this.reloadView)
  },

  computed: {
    ...mapGetters([
      'appConfig'
    ]),

    showOrganizationSelector() {
      // on exclut la view organization
      if(this.$route.matched.some(a => a.path === '/organizations/:id')) {
        return false
      }


      return ![
        'cdr-dashboard',
        'cdr-detail',
        'pending-cdrs.correction',
        'proforma-invoices',
        'proforma-invoices.edit',
        'margin-manager',
        'operator-groups',
        'subscription-plans',
        'agreements',
        'vat-rates',
        'gireve.import-tariffs',
        'price-index',
        'electricity-index',
        'invoices',
        'charging-point-types',
        'reports.exports',
        'reports.meter-device-statements',
        'billing-requests',
        'billing-requests.invoices',
        'billing-requests.pre-invoicing',
        'operators',
        'reports.revenue-cutoff',
        'my-account',
      ].includes(this.$route.name);
    },

    // Key for the ROUTER-VIEW inside the (unkeyed, therefore stable) keep-alive. We key on the
    // top-level PAGE route record (matched[1] — matched[0] is the Default layout itself) resolved
    // with its params, e.g. "/organizations/42". This is stable across a page's own child routes
    // (the OrganizationDetail tabs all share "/organizations/42"), so a kept-alive page survives
    // drilling into a sibling detail page (e.g. /vehicles/:id) and back. It still differs per param
    // (/vehicles/A vs /vehicles/B) so non-cached detail pages remount as before. Query-string-only
    // changes no longer remount, which is what we want for paginated lists.
    routeViewKey() {
      const top = this.$route.matched[1]
      if (!top) return this.$route.fullPath
      return top.path.replace(/:(\w+)/g, (_, k) => this.$route.params[k] ?? `:${k}`)
    },

    cachedPages() {
      // Kept alive unconditionally: OrganizationDetail hosts the org listing tabs (vehicles,
      // members, charging points…). It must stay cached even while you're on a sibling detail page
      // (e.g. /vehicles/:id, which doesn't match any substring below), otherwise the include change
      // would prune it and the listing state would be lost on the way back.
      let pages = ['OrganizationDetail']

      if (this.$route.fullPath.includes('customer')) {
        pages.push('CustomerSearch')
      }

      if (this.$route.fullPath.includes('charging-point')) {
        pages.push('ChargingPointSearch')
      }

      if (this.$route.fullPath.includes('issue')) {
        pages.push('Issues')
      }

      if (this.$route.fullPath.includes('organizations')) {
        pages.push('OrganizationSearch')
      }

      if (this.$route.fullPath.includes('proforma-invoices')) {
        pages.push('ProformaInvoices')
      }

      if (this.$route.fullPath.includes('/invoices')) {
        pages.push('Invoices')
      }


      if (this.$route.fullPath.includes('/billing-requests')) {
        pages.push('BillingRequestSearch')
        pages.push('BillingRequestPreInvoicing')
        pages.push('BillingRequestInvoices')
      }

      return pages
    },
  },

  methods: {
    showLoading(show) {
      this.loading = show
    },

    consolidateOrganization() {
      this.showLoading(true)


      let data = {
        selectedOrganizationConsolidated: this.isConsolidate,
        selectedOrganizationId: this.selectedOrganization
      }
      SecurityRepository.consolidateOrganization(data).then(result => {
        this.reloadView()
        this.showLoading(false)
      }).catch(err => {
        this.notifyError(err)
        this.showLoading(false)
      })
    },

    reloadView() {
      this.showContent = false
      this.$nextTick(() => {
        this.showContent=true;
      });
    },

    onTreeOrganizationUpdated(options) {
      let countElems = 0
      if(options.length > 1) {
        countElems = options.length
      }  else {
        for(let i = 0; i < options.length; i++) {
          countElems++
          if(options[i].children) {
            countElems += options[i].children.length
          }
        }
      }
      this.displayConsolidation = countElems > 1
    }
  }

};
</script>

<style lang="scss">
.v-application {
  background-color: #f8f8f8 !important;

  .v-main {
    .v-main__wrap {
      & > .container {
        display: flex;
        min-width: calc(100vw - 121px);
        min-height: 100%;
        flex-direction: column;
        padding: 0 !important;
      }
    }
  }

  .consolidate-menu {
    display: flex;
    height: 60px;
    align-items: center;
    justify-content: flex-end;
    background-color: white;
    box-shadow: 1px 1px 5px #ccc;
    .select-organization {
      min-width: 500px;
      margin-right: 10px;
    }
    .inner {
      display: flex;
      align-items: center;
      margin-right: 30px;
      .v-input {
        padding-top: 0;
        margin-top: 0;
      }
    }
  }
}

</style>


